import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { Invoice } from '@modules/universal-billing/billing/invoice/invoices.interfaces';
import { DatePipe, DecimalPipe, NgForOf } from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import { Measure } from '@common/pipe/unit-measurement';
import { MoneyFormatPipe } from '@common/pipe/money-format.pipe';
import { ButtonModule } from '@primeng/button';
import { downloadBlob } from '@shared/utils/file.utils';
import { InvoicesService } from '@modules/universal-billing/billing/invoice/invoices.service';
import { UnitTypeDirective } from '@common/directive/unit-type.directive';
import {SweetAlertService} from '@shared/services/sweet-alert.service';
import {AuthService} from '@app/auth/auth.service';
import {CitiesService} from '@modules/settings/cities/cities.service';
import { UnitTypes } from '@shared/enums/UnitTypes.enum';
import urlJoin from 'url-join';
import {environment} from '@environments/environment';
import {HttpClient} from '@angular/common/http';
import { TranslateRolePipe } from '@common/pipe/translateRole.pipe';
import { RenterNamePipe } from '@common/pipe/renter-name.pipe';
import { ClientType } from '@modules/universal-billing/renters/renters.enum';
import {CustomNumberPipe} from '@common/pipe/custom-number.pipe';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'invoice-card',
  standalone: true,
  imports: [
    DatePipe,
    TranslatePipe,
    Measure,
    MoneyFormatPipe,
    ButtonModule,
    UnitTypeDirective,
    NgForOf,
    TranslateRolePipe,
    DecimalPipe,
    RenterNamePipe,
    CustomNumberPipe
  ],
  templateUrl: './invoice-card.component.html',
  styleUrl: './invoice-card.component.scss'
})
export class InvoiceCardComponent implements OnInit {
  constructor(
    private sanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private invoiceService: InvoicesService,
    private sweetAlertService: SweetAlertService,
    private authService: AuthService,
    private citiesService: CitiesService,
    private translateService: TranslateService
  ) {}

  @Input() invoice: Invoice;

  downloading = signal(false);
  qrCodeUrl: string = ''
  qrCodeSvg = null

  salutationFem=['Mrs','Dr (Female)','Prof (Female)','Frau','Frau Dr.','Frau Prof.']
  public buildingAddress;
  public configuration;
  public city;
  public company;
  public bankName: string = '-';

  ngOnInit(): void {
    const apartment = this.invoice?.resident?.apartment;
    this.buildingAddress = this.invoice.resident?.apartment?.building?.addressArray

    this.authService.getMe().subscribe((res) => {
      this.configuration = res['user'].configurations
      this.company=res['user'].company
      this.citiesService.getOne(this.configuration['cityId']).subscribe(city => {
        this.city = city['name'];
      })

      this.httpClient.get<any>(urlJoin(environment.apiUrl, `v1/banks`), { params: { iban: this.configuration['bankIban'] }}).subscribe(result => {
        this.bankName = result?.data?.data[0]['name'];
      })
    })

    this.qrCodeUrl = urlJoin(environment.apiUrl, (this.invoice['id']).toString(), 'pdf');

    this.httpClient.get(urlJoin(environment.apiUrl, 'invoices', this.invoice['id'].toString(), 'qr-code'), { responseType: 'text' })
      .subscribe(result => {
        this.qrCodeSvg = this.sanitizer.bypassSecurityTrustHtml(result);
      });
  }

  getType(id) {
    switch (id) {
      case UnitTypes.HEATING:
        return this.translateService.instant('METER.HEATING');
      case UnitTypes.COLD_WATER:
        return this.translateService.instant('METER.COLD_WATER');
      case UnitTypes.WARM_WATER:
        return this.translateService.instant('METER.WARM_WATER');
      default:
        return this.translateService.instant('METER.RENT');
    }
  }

  protected readonly Math = Math;

  print() {
    this.invoiceService.downloadPdf(this.invoice.id).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = blobUrl;

        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };

        document.body.appendChild(iframe);
      },
      error: (error) => {
        this.sweetAlertService.error('Error loading invoice')
      },
      complete: () => {}
    })
  }

  download() {
    this.downloading.set(true);
    this.invoiceService.downloadPdf(this.invoice.id).subscribe({
      next: (blob) => {
        this.downloading.set(false);
        downloadBlob(blob, `${this.invoice.invoiceNumber}.pdf`);
      },
      error: (error) => {
        this.downloading.set(true);

        if (error.error instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorText = reader.result as string;
              const errorJson = JSON.parse(errorText);
              const message = errorJson.message || this.translateService.instant('ERROR_DOWNLOADING_CONTRACT');
              this.sweetAlertService.error(message);
            } catch (e) {
              this.sweetAlertService.error(this.translateService.instant('UNKNOWN_ERROR_FORMAT'));
            }
          };
          reader.onerror = () => {
            this.sweetAlertService.error('Error reading error response');
          };
          reader.readAsText(error.error);
        } else {
          const fallback = typeof error.error === 'string'
            ? error.error
            : error?.message || this.translateService.instant('ERROR_DOWNLOADING_CONTRACT');
          this.sweetAlertService.error(fallback);
        }
      },
      complete: () => {}
    })
  }


  protected readonly ClientType = ClientType;

  getSalutationText(): string {
    if (this.invoice?.contract?.renter?.clientType !== ClientType.PRIVATE) return '';
    const salutation = this.invoice?.contract?.renter?.salutation?.name;
    return this.salutationFem.includes(salutation)
      ? 'INVOICES.DEAR_MISS'
      : 'INVOICES.DEAR_MR';
  }

}
